import type { ChatPrimeRequest } from "@swivl-mono/types";
import { ReactNode, createContext, useContext } from "react";

import { UseChatResult, useChat } from "./useChat";

/**
 * Hoists the chat connection above the chat panel. `useChat` used to run inside
 * `ChatWindow`, so minimizing the widget (which unmounts `ChatWindow`) tore down
 * the socket. Mounting this provider at a persistent level instead — and having
 * `ChatWindow` read the connection from context — lets the socket survive
 * minimize / surface changes and stay connected for as long as the provider is
 * mounted. See docs/snowy/plans/2026-06-18-widget-presence-lifecycle.md (Phase 1d).
 */
const ChatContext = createContext<UseChatResult | null>(null);

interface ChatProviderProps {
  server: string;
  token: string | null;
  userId: number | null;
  orgId: number;
  channel?: string;
  isTest?: boolean;
  /** Whether the chat panel is currently open (gates read-receipts; see useChat). */
  panelOpen?: boolean;
  /** The v4 widget's publicId — forwarded so the bot applies the widget's display rules (ENG-2753). */
  publicId?: string;
  /** Open primed (ENG-3112 / ENG-3166) — forwarded to the first handshake only. */
  prime?: ChatPrimeRequest;
  /** See useChat's onIdentified — the in-chat-verify → Account-tab sync hook (ENG-3190). */
  onIdentified?: (payload: { userId: number; name?: string; chatToken?: string }) => void;
  children: ReactNode;
}

export function ChatProvider({
  server,
  token,
  userId,
  orgId,
  channel,
  isTest,
  panelOpen = true,
  publicId,
  prime,
  onIdentified,
  children
}: ChatProviderProps): React.ReactElement {
  const chat = useChat(server, token, userId, orgId, channel, isTest, panelOpen, publicId, prime, onIdentified);
  return <ChatContext.Provider value={chat}>{children}</ChatContext.Provider>;
}

/** Read the hoisted chat connection. Must be rendered under a `ChatProvider`. */
export function useChatContext(): UseChatResult {
  const ctx = useContext(ChatContext);
  if (!ctx) {
    throw new Error("useChatContext must be used within a <ChatProvider>");
  }
  return ctx;
}

/**
 * Like `useChatContext` but returns `null` instead of throwing when there's no
 * provider — for components (e.g. the launcher) that render both inside and
 * outside the connection (a passive visitor who never engaged has no provider).
 */
export function useOptionalChatContext(): UseChatResult | null {
  return useContext(ChatContext);
}
